import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatDate, formatViews, sectionLabels } from "@/lib/content";

interface ArticleCardProps {
  article: Article;
  variant?: "row" | "grid" | "compact";
}

const sectionStyle: Record<string, string> = {
  news: "bg-primary-100 text-primary-700",
  review: "bg-accent-100 text-accent-800",
  industry: "bg-secondary-100 text-secondary-800",
};

function Meta({ article }: { article: Article }) {
  return (
    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-500">
      <span className="flex items-center gap-1">
        <i className="ri-user-line" />
        {article.author}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-calendar-line" />
        {formatDate(article.date)}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-eye-line" />
        {formatViews(article.views)}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = "grid" }: ArticleCardProps) {
  if (variant === "row") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer p-3"
      >
        <div className="w-full sm:w-56 lg:w-64 h-48 sm:h-36 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title}相关配图`}
            title={`${article.brand}${article.title}`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full ${sectionStyle[article.section]}`}
            >
              {sectionLabels[article.section]}
            </span>
            <span className="text-[11px] text-foreground-500">{article.category}</span>
          </div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {article.summary}
          </p>
          <div className="mt-auto pt-3">
            <Meta article={article} />
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-3 cursor-pointer"
      >
        <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title}相关配图`}
            title={`${article.brand}${article.title}`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h4>
          <span className="block mt-1.5 text-xs text-foreground-500">
            {formatDate(article.date)}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="h-48 md:h-52 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title}相关配图`}
          title={`${article.brand}${article.title}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2.5">
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full ${sectionStyle[article.section]}`}
          >
            {sectionLabels[article.section]}
          </span>
          <span className="text-[11px] text-foreground-500">{article.category}</span>
        </div>
        <h3 className="font-heading font-semibold text-base text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors min-h-[3rem]">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200">
          <Meta article={article} />
        </div>
      </div>
    </Link>
  );
}